import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ScSpinner from '../ScSpinner';
import { fetchStages, createStage, updateStage } from '../../studyConfigurationApi';
import { DEFAULT_FIXED_STAGES } from '../../studyConfigurationDefaults';

const STAGE_NAMES = {
  SCHEDULE: 'Schedule',
  IN_PROGRESS: 'InProgress',
  COMPLETED: 'Completed',
  REVIEW: 'Review',
};

const FIXED_CODES = {
  [STAGE_NAMES.SCHEDULE]: 101,
  [STAGE_NAMES.IN_PROGRESS]: 201,
  [STAGE_NAMES.COMPLETED]: 9999,
};

const COMPLETED_CODE = FIXED_CODES[STAGE_NAMES.COMPLETED];
const LOCKED_STAGE_CODES = new Set(Object.values(FIXED_CODES));

const isReviewStage = (stageName) => stageName === STAGE_NAMES.REVIEW;
const isLockedStageCode = (code) => LOCKED_STAGE_CODES.has(code);
const isReviewCode = (code) => code > 201 && code < 9999;

const validateForm = (form, isEdit) => {
  if (!form.displayName?.trim()) return 'Display name is required.';
  if (form.orderNo <= 0) return 'Order must be a positive number.';
  if (!Object.values(STAGE_NAMES).includes(form.stageName)) {
    return 'Stage name must be Schedule, InProgress, Completed, or Review.';
  }
  if (isReviewStage(form.stageName)) {
    if (!isReviewCode(form.stageCode)) {
      return 'Review stage code must be greater than 201 and less than 9999 (e.g. 301, 401).';
    }
  } else {
    const expected = FIXED_CODES[form.stageName];
    if (form.stageCode !== expected) {
      return `${form.stageName} must use stage code ${expected}.`;
    }
  }
  if (isEdit) return null;
  return null;
};

const suggestReviewCode = (rows) => {
  const used = new Set(
    rows.filter((r) => isReviewCode(r.stageCode)).map((r) => r.stageCode)
  );
  for (let code = 301; code < COMPLETED_CODE; code += 100) {
    if (!used.has(code)) return code;
  }
  return 301;
};

const suggestOrderBeforeCompleted = (beforeCompleted, completed) => {
  if (completed?.orderNo > 0) return completed.orderNo;
  const max = beforeCompleted.reduce((m, r) => Math.max(m, r.orderNo ?? 0), 0);
  return max + 1;
};

const splitRows = (rows) => {
  const sorted = [...rows].sort((a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0));
  const completedIdx = sorted.findIndex(
    (r) => r.stageCode === COMPLETED_CODE || r.stageName === STAGE_NAMES.COMPLETED
  );
  if (completedIdx < 0) {
    return { beforeCompleted: sorted, completed: null };
  }
  return {
    beforeCompleted: sorted.slice(0, completedIdx),
    completed: sorted[completedIdx],
  };
};

const StagesTab = ({ projectCode }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState(null);

  const existingFixedNames = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.isFixed) set.add(r.stageName);
    });
    return set;
  }, [rows]);

  const addableStageNames = useMemo(() => {
    const all = [
      STAGE_NAMES.SCHEDULE,
      STAGE_NAMES.IN_PROGRESS,
      STAGE_NAMES.COMPLETED,
      STAGE_NAMES.REVIEW,
    ];
    return all.filter((name) => {
      if (isReviewStage(name)) return true;
      return !existingFixedNames.has(name);
    });
  }, [existingFixedNames]);

  const { beforeCompleted, completed } = useMemo(() => splitRows(rows), [rows]);

  const load = useCallback(async () => {
    if (!projectCode) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchStages(projectCode);
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load stages.');
    } finally {
      setLoading(false);
    }
  }, [projectCode]);

  useEffect(() => { load(); }, [load]);

  const applyStageName = (name) => {
    if (isReviewStage(name)) {
      setDraft((prev) => ({
        ...prev,
        stageName: name,
        stageCode: prev.stageCode > 201 && prev.stageCode < 9999
          ? prev.stageCode
          : suggestReviewCode(rows),
      }));
    } else {
      setDraft((prev) => ({
        ...prev,
        stageName: name,
        stageCode: FIXED_CODES[name],
      }));
    }
  };

  const startEdit = (row) => {
    setEditingKey(row.stageMstNo);
    setDraft({
      projectCode,
      stageMstNo: row.stageMstNo,
      stageCode: row.stageCode,
      stageName: row.stageName,
      displayName: row.displayName,
      orderNo: row.orderNo,
    });
    setError(null);
  };

  const startNew = () => {
    const defaultName = addableStageNames.includes(STAGE_NAMES.REVIEW)
      ? STAGE_NAMES.REVIEW
      : addableStageNames[0];
    if (!defaultName) return;
    const stageCode = isReviewStage(defaultName)
      ? suggestReviewCode(rows)
      : FIXED_CODES[defaultName];
    setEditingKey('new');
    setDraft({
      projectCode,
      stageMstNo: null,
      stageCode,
      stageName: defaultName,
      displayName: '',
      orderNo: suggestOrderBeforeCompleted(beforeCompleted, completed),
    });
    setError(null);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft(null);
  };

  const handleSave = async () => {
    if (!draft) return;
    const validationError = validateForm(draft, editingKey !== 'new');
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = { ...draft, projectCode };
      if (editingKey === 'new') await createStage(payload);
      else await updateStage(editingKey, payload);
      setEditingKey(null);
      setDraft(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const seedDefaultStages = async () => {
    if (!projectCode) return;
    setError(null);
    setSuccess(null);
    const existing = new Set(rows.map((r) => r.stageCode));
    const missing = DEFAULT_FIXED_STAGES.filter((s) => !existing.has(s.stageCode));
    const alreadyCount = DEFAULT_FIXED_STAGES.length - missing.length;

    if (missing.length === 0) {
      setSuccess('All default stages already exist.');
      return;
    }

    setSeeding(true);
    let inserted = 0;
    let failed = 0;
    let lastError = null;

    for (const stage of missing) {
      try {
        await createStage({
          projectCode,
          stageCode: stage.stageCode,
          stageName: stage.stageName,
          displayName: stage.displayName,
          orderNo: stage.orderNo,
        });
        inserted += 1;
      } catch (e) {
        failed += 1;
        lastError = e.response?.data?.error || 'Insert failed.';
      }
    }

    setSeeding(false);
    await load();

    if (inserted > 0) {
      const parts = [`Inserted ${inserted} stage${inserted === 1 ? '' : 's'}.`];
      if (alreadyCount > 0) parts.push(`${alreadyCount} already existed.`);
      if (failed > 0) parts.push(`${failed} failed.`);
      setSuccess(parts.join(' '));
      if (failed > 0 && lastError) setError(lastError);
    } else if (lastError) {
      setError(lastError);
    }
  };

  const renderStageRow = (row) => {
    const editable = editingKey === row.stageMstNo;
    const d = editable ? draft : row;
    const isReview = isReviewStage(d.stageName);
    const codeReadOnly = editable && (editingKey !== 'new' || !isReview);

    return (
      <tr key={row.stageMstNo} className={editable ? 'sc-row-editing' : ''}>
        <td>
          {editable ? (
            <input
              type="number"
              className="sc-inline-input sc-inline-input-sm"
              value={d.stageCode}
              readOnly={codeReadOnly}
              disabled={codeReadOnly || saving}
              onChange={(e) => setDraft({ ...draft, stageCode: Number(e.target.value) })}
            />
          ) : (
            row.stageCode
          )}
        </td>
        <td>
          {editable && editingKey === 'new' ? (
            <select
              value={d.stageName}
              disabled={saving}
              onChange={(e) => applyStageName(e.target.value)}
            >
              {addableStageNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          ) : (
            row.stageName
          )}
        </td>
        <td>
          {editable ? (
            <input
              className="sc-inline-input"
              value={d.displayName}
              disabled={saving}
              onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
            />
          ) : (
            row.displayName
          )}
        </td>
        <td>
          {editable ? (
            <input
              type="number"
              className="sc-inline-input sc-inline-input-sm"
              value={d.orderNo}
              disabled={saving}
              onChange={(e) => setDraft({ ...draft, orderNo: Number(e.target.value) })}
            />
          ) : (
            row.orderNo
          )}
        </td>
        <td className="sc-actions-cell">
          {editable ? (
            <>
              <button
                type="button"
                className="sc-btn sc-btn-primary sc-btn-sm"
                disabled={saving}
                onClick={handleSave}
              >
                Save
              </button>
              <button
                type="button"
                className="sc-btn sc-btn-secondary sc-btn-sm"
                disabled={saving}
                onClick={cancelEdit}
              >
                Cancel
              </button>
            </>
          ) : !isLockedStageCode(row.stageCode) ? (
            <button
              type="button"
              className="sc-btn sc-btn-link"
              disabled={editingKey != null}
              onClick={() => startEdit(row)}
            >
              Edit
            </button>
          ) : null}
        </td>
      </tr>
    );
  };

  const renderNewRow = () => {
    const editingNew = editingKey === 'new';
    const d = draft;
    const isReview = d && isReviewStage(d.stageName);
    const orderLockedForNewReview = editingNew && isReview && !!completed;

    if (editingNew && d) {
      return (
        <tr className="sc-row-editing sc-row-new">
          <td>
            <input
              type="number"
              className="sc-inline-input sc-inline-input-sm"
              value={d.stageCode}
              readOnly={!isReview}
              disabled={!isReview || saving}
              onChange={(e) => setDraft({ ...draft, stageCode: Number(e.target.value) })}
            />
          </td>
          <td>
            <select
              value={d.stageName}
              disabled={saving}
              onChange={(e) => applyStageName(e.target.value)}
            >
              {addableStageNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </td>
          <td>
            <input
              className="sc-inline-input"
              value={d.displayName}
              disabled={saving}
              placeholder={isReview ? 'e.g. First Review' : ''}
              onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
            />
          </td>
          <td>
            {orderLockedForNewReview ? (
              <span className="sc-order-auto" title="Completed order updates automatically on save.">
                {d.orderNo}
              </span>
            ) : (
              <input
                type="number"
                className="sc-inline-input sc-inline-input-sm"
                value={d.orderNo}
                disabled={saving}
                onChange={(e) => setDraft({ ...draft, orderNo: Number(e.target.value) })}
              />
            )}
          </td>
          <td className="sc-actions-cell">
            <button
              type="button"
              className="sc-btn sc-btn-primary sc-btn-sm"
              disabled={saving}
              onClick={handleSave}
            >
              Save
            </button>
            <button
              type="button"
              className="sc-btn sc-btn-secondary sc-btn-sm"
              disabled={saving}
              onClick={cancelEdit}
            >
              Cancel
            </button>
          </td>
        </tr>
      );
    }

    return (
      <tr className="sc-row-new">
        <td>—</td>
        <td>—</td>
        <td>—</td>
        <td>—</td>
        <td className="sc-actions-cell">
          <button
            type="button"
            className="sc-btn sc-btn-link"
            disabled={editingKey != null}
            onClick={startNew}
          >
            New
          </button>
        </td>
      </tr>
    );
  };

  const showNewRow = addableStageNames.length > 0;

  if (!projectCode) {
    return (
      <p className="sc-empty">
        No project in session. Change project from Visit or Site pages (shown in the header).
      </p>
    );
  }
  if (loading) return <ScSpinner />;

  return (
    <div className="sc-stages">
      {error && <div className="sc-alert sc-alert-error">{error}</div>}
      {success && <div className="sc-alert sc-alert-success">{success}</div>}
      <div className="sc-toolbar">
        <button
          type="button"
          className="sc-btn sc-btn-secondary"
          onClick={seedDefaultStages}
          disabled={seeding || editingKey != null}
        >
          {seeding ? 'Inserting…' : 'Insert Default Stages'}
        </button>
      </div>

      <div className="sc-grid-wrap">
        <table className="sc-grid">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Display</th>
              <th>Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {beforeCompleted.map((row) => renderStageRow(row))}
            {showNewRow && renderNewRow()}
            {completed && renderStageRow(completed)}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && !showNewRow && (
        <p className="sc-empty">No workflow stages configured for this project.</p>
      )}
    </div>
  );
};

export default StagesTab;
